import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/system';
import type { UseUserCreate } from '@openx/components-administration/lib/utils/context/UserHookTypes';
import { Button } from '@openx/components-core/lib/Button/Button';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getValidationSchema } from './validationSchema';

type CreateUserModalFormData = {
  first_name: string;
  last_name: string;
  email_address: string;
};

export type CreateUserModalFormProps = {
  handleCreateUser: ReturnType<UseUserCreate>['createUser'];
  organizationId: string;
  organizationParentAccountId: string | undefined;
};

const defaultValues: CreateUserModalFormData = {
  first_name: '',
  last_name: '',
  email_address: '',
};

export const CreateUserModalForm = ({
  handleCreateUser,
  organizationId,
  organizationParentAccountId,
}: CreateUserModalFormProps) => {
  const { t } = useTranslation();

  const formMethods = useForm<CreateUserModalFormData>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const handleSubmit = async (data: CreateUserModalFormData) => {
    const type = organizationParentAccountId ? 'parent' : 'root';
    const { first_name, last_name, email_address } = data;

    await handleCreateUser({
      type,
      user: {
        email_address: email_address,
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        parent_account_id: organizationId,
      },
      shouldRedirect: false,
      onSuccess: formMethods.reset,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form data-test="new-user-form" autoComplete="off" noValidate onSubmit={formMethods.handleSubmit(handleSubmit)}>
        <Stack spacing={2} direction="row" sx={{ mb: 2, alignItems: 'baseline', justifyContent: 'space-between' }}>
          <RHFTextField name={'first_name'} label={t('First Name')} placeholder={t('Enter first name')} />
          <RHFTextField name={'last_name'} label={t('Last Name')} placeholder={t('Enter last name')} />
          <RHFTextField name={'email_address'} label={t('Email Address')} placeholder={t('Enter email address')} />

          <Button
            color="primary"
            variant="contained"
            loading={formMethods.formState.isSubmitting}
            type="submit"
            sx={{ flexShrink: 0 }}
          >
            {t('Invite')}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};
